import { FC, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import ponchoImg from '../../media/poncho.png';
import jaroImg from '../../media/jaro.png';
import basiaImg from '../../media/basia.png';
import zusiaImg from '../../media/zusia.png';
import { Typography, Popper, Box, ClickAwayListener } from '@mui/material';
import useTranslations from '../global/translation/hooks/translation';
import { JobModal } from '../global/JobModal';
import { useThemeContext } from '../global/ThemeContext';
import useImagePreload from '../../hooks/useImagePreload';

const Team: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const percentAmount = '8rem';
  const { darkMode } = useThemeContext();
  const [selectedMember, setSelectedMember] = useState<{ name: string; description: string } | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);
  const preloadImages = useMemo(() => [ponchoImg, basiaImg, jaroImg, zusiaImg], []);

  // Warm the cache for team images on first mount
  useImagePreload(preloadImages);

  const handleClose = () => {
    setSelectedMember(null);
    setAnchorEl(null);
  };

  const teamMembers = [
    {
      name: currentLanguage === 'pl' ? 'Poncho' : 'Poncho',
      description: translations.label?.teamPonchoDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Koń treningowy' : 'Training Horse'),
      img: ponchoImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Barbara' : 'Barbara',
      description: translations.label?.teamBasiaDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Główny szkoleniowiec' : 'Head Trainer'),
      img: basiaImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Jarosław' : 'Jarosław',
      description: translations.label?.teamJaroDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Asystent trenera' : 'Trainer Assistant'),
      img: jaroImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Zuzanna' : 'Susanne',
      description: translations.label?.teamZosiaDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Opiekun koni' : 'Horse Care Specialist'),
      img: zusiaImg,
    },
  ];
  return (
    <Grid
      my={6}
      py={{ xs: 6, md: 10 }}
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        bgcolor: darkMode ? '#121212' : '#FFFFFF',
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 2, md: 4 },
      }}
    >
      <Grid item xs={12} mb={4}>
        <Typography variant="h4" fontWeight={300} sx={{ color: darkMode ? '#FFFFFF' : '#000000', letterSpacing: '-0.5px' }} textAlign="center">
          {translations.menuItems.ourTeam[currentLanguage]}
        </Typography>
      </Grid>
      <Grid container justifyContent="center" alignItems="center" onClick={handleClose}>
        {teamMembers.map((member, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: percentAmount,
                m: 2,
                cursor: 'pointer',
                transition: 'transform 0.3s ease-in-out',
                transform: 'translateY(0)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMember(member);
                setAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'scale-down',
                  transition: 'transform 0.3s ease',
                }}
                src={member.img}
                alt={member.name}
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                width={256}
                height={256}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs>
            <JobModal percentAmount={percentAmount} />
          </Grid>
        </Grid>
      </Grid>

      <Popper
        open={!!selectedMember}
        anchorEl={anchorEl}
        placement="bottom"
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: [0, 12],
            },
          },
        ]}
        sx={{
          zIndex: 1300,
          '&[data-popper-placement*="bottom"] .MuiPopper-arrow': {
            top: 0,
            left: 0,
            marginTop: '-0.71em',
            width: '1em',
            height: '0.71em',
            '&::before': {
              content: '""',
              margin: 'auto',
              display: 'block',
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 0.71em 0.71em 0.71em',
              borderColor: `transparent transparent ${darkMode ? '#232323' : '#FFFFFF'} transparent`,
            },
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown">
          <Box
            sx={{
              backgroundColor: darkMode ? '#232323' : '#FFFFFF',
              border: `1px solid ${darkMode ? '#333333' : '#EEEEEE'}`,
              boxShadow: darkMode ? '0 8px 16px rgba(0, 0, 0, 0.3)' : '0 8px 16px rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              p: 3,
              position: 'relative',
              maxWidth: '400px',
            }}
          >
            <Box ref={setArrowRef} className="MuiPopper-arrow" />
            <Typography
              variant="h6"
              sx={{
                color: darkMode ? '#FFFFFF' : '#000000',
                fontWeight: 400,
                mb: 1.5,
                fontSize: '18px',
                letterSpacing: '-0.3px',
              }}
            >
              {selectedMember?.name}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: darkMode ? '#CCCCCC' : '#555555',
                lineHeight: 1.7,
                fontSize: '15px',
              }}
            >
              {selectedMember?.description}
            </Typography>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Grid>
  );
};

export default Team;
